/**
 * Security tab — change password + log out everywhere.
 *
 * Change-password form mirrors the standalone /account/change-password
 * page (which now redirects here). Log-out-everywhere bumps the user's
 * `token_version` server-side, invalidating every existing JWT — caller
 * is bounced to /login on success.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SecurityTab = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const sameAsCurrent = current && next === current;
  const mismatch = confirm && next !== confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (sameAsCurrent) { toast.error('New password must differ from current.'); return; }
    if (mismatch) { toast.error('Passwords do not match.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/me/change-password`, {
        current_password: current, new_password: next,
      }, { withCredentials: true });
      toast.success('Password changed. Other sessions have been logged out.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change password.');
    } finally {
      setSaving(false);
    }
  };

  const logoutEverywhere = async () => {
    if (!window.confirm('Log out everywhere?\n\nEvery device currently signed into this account will be signed out. You will be redirected to the login page.')) return;
    setLoggingOut(true);
    try {
      await axios.post(`${API}/account/logout-everywhere`, {}, { withCredentials: true });
      toast.success('All sessions logged out. Sign back in to continue.');
      // Wait a beat so the toast renders, then drop them on /login.
      setTimeout(() => navigate('/login'), 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not log out everywhere.');
      setLoggingOut(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl" data-testid="security-tab">
      <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="security-change-password-form">
        <h2 className="text-xl font-bold text-[#020617] mb-1">Change password</h2>
        <p className="text-sm text-[#64748B] mb-4">Changing your password signs you out everywhere except this device.</p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="current">Current password</Label>
            <Input
              id="current"
              type={show ? 'text' : 'password'}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-1.5"
              required
              autoComplete="current-password"
              data-testid="security-current-password-input"
            />
          </div>
          <div>
            <Label htmlFor="next">New password</Label>
            <Input
              id="next"
              type={show ? 'text' : 'password'}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-1.5"
              required
              autoComplete="new-password"
              minLength={10}
              data-testid="security-new-password-input"
            />
            {sameAsCurrent && (
              <p className="text-xs text-[#DC2626] mt-1.5" data-testid="security-same-as-current">
                New password must be different from your current one.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input
              id="confirm"
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5"
              required
              autoComplete="new-password"
              data-testid="security-confirm-password-input"
            />
            {mismatch && (
              <p className="text-xs text-[#DC2626] mt-1.5" data-testid="security-mismatch">
                Passwords don&apos;t match.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="inline-flex items-center gap-1.5 text-xs text-[#64748B] hover:text-[#020617]"
            data-testid="security-toggle-visibility"
          >
            {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {show ? 'Hide' : 'Show'} passwords
          </button>
        </div>

        <Button
          type="submit"
          disabled={saving || sameAsCurrent || mismatch}
          className="mt-6 bg-[#0052FF] text-white hover:bg-[#003ECC]"
          data-testid="security-change-password-submit"
        >
          {saving ? 'Updating…' : 'Update password'}
        </Button>
      </form>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="security-sessions-card">
        <h2 className="text-xl font-bold text-[#020617] mb-1">Active sessions</h2>
        <p className="text-sm text-[#64748B] mb-4">
          Don&apos;t recognise a device, or sold a phone with Forge Ledger still signed in? Sign out everywhere — every device including this one will be bounced to the login page.
        </p>
        <Button
          type="button"
          onClick={logoutEverywhere}
          disabled={loggingOut}
          className="bg-[#FEE2E2] text-[#991B1B] hover:bg-[#FECACA] border-0"
          data-testid="security-logout-everywhere-button"
        >
          <LogOut className="w-4 h-4 mr-2" />
          {loggingOut ? 'Logging out…' : 'Log out everywhere'}
        </Button>
      </div>
    </div>
  );
};

export default SecurityTab;
